<template>
    <div class="pvp-battle-screen" :style="battlefieldBackgroundStyle">
        <div v-if="!match" class="pvp-battle-screen-loading pa-6 text-center">
            <!-- Zadani uzivatele 2026-08-20 - "kdyz refreshnu, tak se mi tam
                 jen toci kolecko" - GET /pvp/{id} muze tise selhat (viz
                 refresh()/handleDeadMatch()) a rovnou presmerovat pryc, takze
                 tenhle spinner uz nema kdy zustat viset navzdy. -->
            <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
            <v-alert v-if="errorMessage" type="error" dense dismissible class="ma-2" @input="errorMessage = ''">
                {{ errorMessage }}
            </v-alert>

            <!-- PENDING - vyzva jeste neni prijata/odmitnuta -->
            <div v-if="match.status === 'PENDING'" class="pvp-battle-screen-status pa-6 text-center">
                <v-card class="pvp-battle-screen-status-card pa-4" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'">
                    <template v-if="!match.isChallenger">
                        <p class="pvp-battle-screen-status-text">
                            <strong>{{ otherTrainer.name }}</strong> {{ $t('pvp.battleScreen.pendingChallengeIntroSuffix', {matchSize: $tc('pvp.common.pokemonCount', match.matchSize, {count: match.matchSize})}) }}
                        </p>
                        <div class="d-flex justify-center" style="gap: 12px;">
                            <v-btn color="error black--text btn-shadow" :loading="isBusy" @click="doDecline">{{ $t('pvp.common.decline') }}</v-btn>
                            <v-btn color="primary black--text btn-shadow" :loading="isBusy" @click="doAccept">{{ $t('pvp.common.accept') }}</v-btn>
                        </div>
                    </template>
                    <template v-else>
                        <p class="pvp-battle-screen-status-text">{{ $t('pvp.battleScreen.waitingResponsePrefix') }} <strong>{{ otherTrainer.name }}</strong> {{ $t('pvp.battleScreen.waitingResponseSuffix') }}</p>
                        <v-progress-circular indeterminate color="primary" size="28" class="mb-3" />
                        <!-- Zadani uzivatele 2026-08-19 - "poslanou vyzvu
                             nejde zrusit" - doDecline() uz existuje (pouzivane
                             vyzvanym pro Odmitnout), backend ted povoluje
                             stejnou akci i challengerovi (viz PvpService::decline). -->
                        <div>
                            <v-btn color="error black--text btn-shadow" small :loading="isBusy" @click="doDecline">{{ $t('pvp.battleScreen.cancelChallenge') }}</v-btn>
                        </div>
                    </template>
                </v-card>
            </div>

            <!-- DECLINED -->
            <div v-else-if="match.status === 'DECLINED'" class="pvp-battle-screen-status pa-6 text-center">
                <v-card class="pvp-battle-screen-status-card pa-4" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'">
                    <!-- Finalni whole-branch review (opus) - backend ma jeden
                         DECLINED stav pro odmitnuti souperem, zruseni
                         challengerem i timeout, ale "odmítnuta" pusobilo
                         divne kdyz si challenger sam zrusil vlastni vyzvu.
                         match.isChallenger rika jen kdo challenge poslal, ne
                         proc skoncila jako DECLINED - text je proto neutralni
                         "zrušena" na challengerove strane (pokryva sebe-cancel
                         i timeout), "odmítnuta" zustava na opponentove strane. -->
                    <p class="pvp-battle-screen-status-text">{{ match.isChallenger ? $t('pvp.battleScreen.challengeCancelled') : $t('pvp.battleScreen.challengeDeclined') }}</p>
                    <v-btn color="primary black--text btn-shadow" @click="goToHub">{{ $t('pvp.common.backToArena') }}</v-btn>
                </v-card>
            </div>

            <!-- ACTIVE i FINISHED - sdileny battle layout (zadani uzivatele
                 2026-08-19 - "chtel bych aby po fightu zustali idealne
                 pokemoni a nejakej vysledek toho fightu... je naprd kdyz
                 posledni finalni utok nema vlastni data zobrazeny, protoze
                 ten fight uz skoncil"). Driv FINISHED mel vlastni samostatnou
                 vetev (jen text + tlacitka), ktera zahodila cely vizual
                 souboje - prestoze `currentPokemonFight` (a tedy
                 fight.myPokemon/opponentPokemon/lastRoundReport) se pri
                 prechodu na FINISHED na backendu NIKDY nemaze (viz
                 PvpService::advanceAfterSubFightEnd() - jen se zapise
                 vysledek/status, `currentPokemonFight` zustava). Data uz tedy
                 vzdy byla k dispozici, jen FE si o ne rekl. Ted PENDING/
                 DECLINED zustavaji svoje samostatne vetve, ale ACTIVE i
                 FINISHED sdili stejny BattleLayout (fight.myPokemon/
                 opponentPokemon jsou v obou stavech vyplnene, chooseMode je
                 tedy vzdy false) - jedina zmena je v #skill-actions patce:
                 misto SkillActionBar se na FINISHED zobrazi vysledek +
                 tlacitka. Pokud se hrac na zapas vrati pozdeji (pred
                 pripadnym prunOldMatches pri dalsi vyzve), uvidi presne
                 tohle same - posledni pokemon proti poslednimu, s vyslednym
                 HP a report kolem posledniho utoku. -->
            <div v-else class="pvp-battle-screen-active">
                <BattleLayout :choose-mode="chooseMode">
                    <template #player>
                        <CombatantSlot
                            v-if="!fight.myPokemon && match.status === 'ACTIVE'"
                            kind="choose"
                            min-height="100%"
                            @open-picker="pickerOpen = true"
                        />
                        <CombatantSlot
                            v-else-if="myPokemonView"
                            kind="pokemon"
                            :fight="!chooseMode"
                            :pokemon="myPokemonView"
                            :opponent-pokemon="opponentPokemonView"
                            min-height="100%"
                            side="Left"
                        />
                        <!-- Zapas skoncil (napr. souper se vzdal) drive, nez
                             jsem stihl vybrat pokemona do tohohle paru -
                             fight.myPokemon zustava NULL navzdy (nalez
                             uzivatele 2026-08-26). Zadny picker (uz neni co
                             vybirat), jen informativni placeholder misto
                             navzdy tocicicho se kolecka. -->
                        <v-card v-else-if="!fight.myPokemon" class="pvp-battle-screen-waiting-card" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'" height="100%">
                            <span>{{ $t('pvp.battleScreen.noPokemonUsedLabel') }}</span>
                        </v-card>
                        <v-card v-else class="pvp-battle-screen-waiting-card" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'" height="100%">
                            <v-progress-circular indeterminate color="primary" size="24" />
                        </v-card>
                    </template>

                    <template #opponent>
                        <CombatantSlot
                            v-if="fight.opponentPokemon && opponentPokemonView"
                            kind="pokemon"
                            :pokemon="opponentPokemonView"
                            :opponent-pokemon="fight.myPokemon ? myPokemonView : null"
                            :show-details="!!fight.myPokemon"
                            min-height="100%"
                            side="Right"
                        />
                        <v-card v-else class="pvp-battle-screen-waiting-card" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'" height="100%">
                            <span v-if="!fight.opponentPokemon && fight.opponentHasChosenPokemon">{{ otherTrainer.name }} {{ $t('pvp.battleScreen.opponentChosenWaitingSuffix') }}</span>
                            <span v-else-if="!fight.opponentPokemon">{{ otherTrainer.name }} {{ $t('pvp.battleScreen.opponentChoosingSuffix') }}</span>
                            <v-progress-circular v-else indeterminate color="primary" size="24" />
                        </v-card>
                    </template>

                    <template v-if="!chooseMode" #center>
                        <FightReport
                            :fight="fightReportAdapter"
                            :is-trainer-fight="isTrainerFightAdapter"
                            :waiting="waitingForOpponent"
                            :opponent-trainer-id="otherTrainer.id"
                            :deadline="fight.roundDeadlineAt"
                        />
                    </template>

                    <template v-if="!chooseMode" #skill-actions>
                        <!-- Zadani uzivatele 2026-08-20 - stale-fight refresh
                             NEMA byt automaticky/tichy (viz submitSkill catch)
                             - misto attack tlacitek se zobrazi tenhle bar s
                             vyzvou ke kliknuti. Duvod: automaticky refresh by
                             mohl hrace prekvapive odnavigovat/prekreslit i v
                             situacich, kdy si toho nevsimne (napr. soubezne
                             vzajemne vyzvy - "muzeme se vyzvat navzajem a ten
                             jeden pak ostrouha"). Zbytek obrazovky (pokemoni,
                             report) zustava do kliknuti nezmeneny. -->
                        <div v-if="staleFightDetected" class="pvp-battle-screen-finished-bar" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'">
                            <p class="pvp-battle-screen-status-text mb-1">{{ $t('pvp.battleScreen.staleFightNotice') }}</p>
                            <v-btn color="accent black--text btn-shadow" small @click="acceptStaleFight">{{ $t('pvp.battleScreen.showNewFight') }}</v-btn>
                        </div>
                        <SkillActionBar
                            v-else-if="match.status !== 'FINISHED'"
                            :skills="currentRoundSkills"
                            :selected-skill-id="selectedSkillId"
                            :can-fight="canFight"
                            :base-label="$t('pvp.battleScreen.baseMove')"
                            :round-role="roundRole"
                            :next-round-hint="nextRoundHint"
                            allow-base-move
                            @open-picker="skillPickerOpen = true"
                            @clear-skill="selectedSkillId = null"
                            @use-skill="useSelectedSkill"
                            @use-base-move="useBaseMove"
                        />
                        <!-- Zadani uzivatele 2026-08-20 - "kdyz mezi tim
                             protivnik me znovu vyzve, tak by se mi mel dole
                             refreshnout okynko vyhral jsi a zobrazi se tam
                             vyzva k zapasu" - jen live-prekresleni spodni
                             listy (viz newChallengeMatch/checkForNewChallenge),
                             zbytek obrazovky (pokemoni, report dohraneho
                             zapasu) zustava beze zmeny. Omezeno jen na
                             vyzvu od STEJNEHO soupere, ktereho prave vidime -
                             viz filtr v checkForNewChallenge. -->
                        <div v-else class="pvp-battle-screen-finished-bar" :class="$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03'">
                            <template v-if="newChallengeMatch">
                                <p class="pvp-battle-screen-status-text mb-1">
                                    <strong>{{ otherTrainer.name }}</strong> {{ $t('pvp.battleScreen.newChallengeIntroSuffix') }}
                                </p>
                                <div class="d-flex justify-center" style="gap: 12px;">
                                    <v-btn color="error black--text btn-shadow" small :loading="isBusy" @click="declineNewChallenge">{{ $t('pvp.common.decline') }}</v-btn>
                                    <v-btn color="primary black--text btn-shadow" small :loading="isBusy" @click="acceptNewChallenge">{{ $t('pvp.common.accept') }}</v-btn>
                                </div>
                            </template>
                            <template v-else>
                                <p class="pvp-battle-screen-status-title mb-1" :class="iWon ? 'pvp-battle-screen-status-title--win' : 'pvp-battle-screen-status-title--lose'">
                                    {{ iWon ? $t('pvp.common.won') : $t('pvp.battleScreen.lost') }}
                                </p>
                                <div class="d-flex justify-center" style="gap: 12px;">
                                    <v-btn color="primary black--text btn-shadow" small @click="goToHub">{{ $t('pvp.common.backToArena') }}</v-btn>
                                    <v-btn color="accent black--text btn-shadow" small :loading="isBusy" @click="doRematch">{{ $t('pvp.battleScreen.rematch') }}</v-btn>
                                </div>
                            </template>
                        </div>
                    </template>

                    <!-- #actions (na rozdil od #skill-actions vidime VZDY,
                         BattleLayout.vue ho neskryva podle chooseMode - zadani
                         uzivatele 2026-08-20 "vzdat se ma byt videt vzdycky",
                         stejne misto v layoutu jako Escape/Recall pod skill
                         tlacitky v singleplayerovem FightButtons.vue). -->
                    <template v-if="match.status === 'ACTIVE'" #actions>
                        <div class="d-flex justify-center pa-2" style="gap: 12px;">
                            <v-btn color="error black--text btn-shadow" x-small :loading="isBusy" @click="doSurrender">{{ $t('pvp.battleScreen.surrender') }}</v-btn>
                            <v-btn v-if="canRecall" color="warning black--text btn-shadow" x-small :loading="isBusy" @click="doRecall">{{ $t('pvp.battleScreen.recallPokemon') }}</v-btn>
                        </div>
                    </template>
                </BattleLayout>
            </div>
        </template>

        <PokemonPickerDialog v-model="pickerOpen" :pokemons="pokemonsToChoose" @choose="onChoosePokemon" />
        <SkillPickerDialog
            v-model="skillPickerOpen"
            :skills="currentRoundSkills"
            :round-role="roundRole"
            @choose="(skill) => selectedSkillId = skill.id"
        />
        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    // PvP bojova obrazovka (zadani uzivatele 2026-08-18, "nahrubo" - viz
    // claude/roadmap.md sekce "PvP souboje mezi hráči"), poskladana ze
    // SKUTECNYCH prezentacnich kousku realne bojove obrazovky, stejny recept
    // jako TutorialFakeFight.vue (BattleLayout/CombatantSlot/SkillActionBar/
    // SkillPickerDialog/PokemonPickerDialog beze zmeny). Na rozdil od
    // tutorialu je to skutecny async souboj mezi dvema realnymi hraci - zadny
    // lokalni skript, jen GET /pvp/{id} (viz backend PvpService,
    // docs/superpowers/specs/2026-08-17-pvp-fights-design.md) - "nerevealuj
    // souperuv tah" princip znamena, ze dokud soupeř svuj tah neodesle, vidime
    // jen `opponentReady:false`, zadny detail.
    //
    // Refresh pres Mercure push, ne polling (zadani uzivatele 2026-08-18,
    // druhe kolo - "polling je nahovno, chovej se jako zpravy/vzkazy") -
    // PvpService po kazde zmene stavu zapasu publikuje na uz existujici
    // privatni topic hrace (`/trainers/{id}/notifications`, stejny jako
    // zpravy mezi hraci) typ `pvp_update` s pouhym {matchId}. Payload
    // schvalne nenese zadna herni data (viz komentar u PvpService::notify) -
    // po prijeti signalu se udela presne jeden GET /pvp/{id}, misto driveho
    // opakovaneho pollovani kazde 4-6s.
    //
    // Detail API vraci pro myPokemon/opponentPokemon jen {id,name,hp,maxHp,
    // skillIds} (PvpPresenter::fightToArray) - bohatsi data (level/sprite/
    // typ/plne skill objekty s dmg/type) se dotahuji zvlast pres uz
    // existujici trainer/loadPokemons (stejna akce jako TeamBottomSheet.vue
    // pouziva pro lokalni tym), jen kdyz se zmeni id (nova volba pokemona/
    // novy sub-fight) - aktualni HP se pak pri kazdem pollu meri do teto
    // hydratovane kopie (viz myPokemonView/opponentPokemonView), stejny vzor
    // jako TutorialFakeFight.vue playerPokemonView/magikarpView.
    //
    // Vycerpane skilly - primo z API (fight.myUsedSkillIds, viz
    // PvpPresenter::fightToArray()). Drivejsi verze (do 2026-08-20) si tohle
    // dopocitavala lokalne z historie lastRoundReport - krehke (ztracelo se
    // po refreshi stranky) a navic spatne pro vitezneho pokemona
    // pokracujiciho do dalsiho sub-fightu (viz komentar u autoSelectSkill()
    // a PvpService::advanceAfterSubFightEnd() - jeho uz pouzite skilly se
    // ted spravne prenaseji, misto aby se s novym PvpPokemonFight resetovaly).
    import BattleLayout from '@/components/journey/positions/battle/BattleLayout';
    import CombatantSlot from '@/components/journey/positions/battle/CombatantSlot';
    import SkillActionBar from '@/components/journey/positions/battle/SkillActionBar';
    import SkillPickerDialog from '@/components/journey/positions/battle/SkillPickerDialog';
    import PokemonPickerDialog from '@/components/journey/positions/battle/PokemonPickerDialog';
    // Fight report (zadani uzivatele 2026-08-18 - "musi byt naprosto stejny
    // jako proti wild pokemonovi/gym trenerovi") - stejna komponenta jako
    // singleplayer, ne vlastni klon. Adaptery nize (fightReportAdapter/
    // isTrainerFightAdapter) prekladaji tenci PvP report na stejny tvar dat,
    // jaky FightReport.vue/FightReportDuel.vue ocekavaji ze singleplayeru.
    import FightReport from '@/components/journey/positions/fight/FightReport';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import mercureClient from '@/utils/mercureClient';
    import {consumePvpSelfAction} from '@/utils/pvpSelfActionTracker';
    import {playNotificationSound} from '@/utils/notificationAudio';
    import {mapActions, mapGetters} from 'vuex';
    import * as pokemonApi from '@/api/pokemonApi';
    import {isHpGateSatisfied} from '@/utils/skillDisplay';

    export default {
        name: 'PvpBattleScreen',
        components: {BattleLayout, CombatantSlot, SkillActionBar, SkillPickerDialog, PokemonPickerDialog, FightReport, ConfirmDialog},
        props: {
            id: {type: [String, Number], required: true},
        },
        data() {
            return {
                match: null,
                errorMessage: '',
                staleFightDetected: false,
                newChallengeMatch: null,
                isBusy: false,
                isResolving: false,
                unsubscribePvpUpdate: null,
                // Vsichni pokemoni (tym + box u Oaka) - pouziva se pro ranked
                // picker (viz pokemonsToChoose), stejny vzor jako pokemonListAll
                // v CityHealDialog.vue (fetchObject, NEplni sdileny pokemon/items).
                allPokemons: [],
                pickerOpen: false,
                skillPickerOpen: false,
                selectedSkillId: null,
                myPokemonFull: null,
                opponentPokemonFull: null,
            }
        },
        computed: {
            ...mapGetters({
                user: 'user/item',
                activeTeam: 'pokemon/items',
                buttonVolume: 'journey/buttonVolume',
            }),
            matchId() {
                return parseInt(this.id, 10);
            },
            fight() {
                return this.match && this.match.fight ? this.match.fight : null;
            },
            // "match.status === 'ACTIVE'" pridano (nalez uzivatele 2026-08-26,
            // viz PvpPresenter.php komentar u concealOtherPokemon) - kdyz
            // soupeř vzda CELY zapas drive, nez jsem stihl vybrat pokemona do
            // aktualniho paru, fight.myPokemon/opponentPokemon muzou zustat
            // NULL i po prechodu na FINISHED. Bez tehle podminky by chooseMode
            // zustal navzdy true (skryva #center/#skill-actions vysledek
            // zapasu, viz template nize) a hrac by se z FINISHED obrazovky
            // nemel jak dostat.
            chooseMode() {
                return !!this.fight && this.match.status === 'ACTIVE' && (!this.fight.myPokemon || !this.fight.opponentPokemon);
            },
            otherTrainer() {
                if (!this.match) {
                    return {name: '', img: null};
                }
                return this.match.isChallenger ? this.match.opponent : this.match.challenger;
            },
            iWon() {
                return this.match && this.match.winner !== null && this.user
                    ? this.match.winner === this.user.id
                    : false;
            },
            mySide() {
                return this.match && this.match.isChallenger ? 'challenger' : 'opponent';
            },
            isRanked() {
                return !!this.match && this.match.rankedChallengeId !== null && this.match.rankedChallengeId !== undefined;
            },
            // Ranked zapas dostane vlastni pozadi (zadani uzivatele 2026-08-26 -
            // obrazek pvp-liga.jpg patri na bojovku, ne na hub), volne PvP
            // zustava u lesniho pozadi.
            battlefieldBackgroundStyle() {
                const image = this.isRanked
                    ? require('@/assets/pic/pvp/battlefield-pvp-liga.jpg')
                    : require('@/assets/pic/pvp/battlefield-forest.jpg');
                return {backgroundImage: 'url(' + image + ')'};
            },
            lastReport() {
                return this.fight ? this.fight.lastRoundReport : null;
            },
            waitingForOpponent() {
                return !!this.fight && this.fight.myPendingSet && !this.fight.opponentReady;
            },
            roundRole() {
                return this.fight && this.fight.iAmAttacker ? 'attack' : 'defence';
            },
            canFight() {
                return !this.chooseMode && !!this.fight && !this.fight.myPendingSet && !this.isResolving;
            },
            // `endangered` prepsano na false stejne jako hp/maxHp vyse (zadani
            // uzivatele 2026-08-20 - "endangered by se mel pocitat ze
            // spravnych zivotu, ne z toho co mam primo ve hre"): myPokemonFull
            // nese realny endangered flag z overworldu (Pokemon::getEndangered(),
            // vyzaduje HP hodne do minusu pod prah), ale PvpService::resolveRound()
            // PvP hp VZDY clampuje na max(0, ...) (radek 680-681) - v PvP tedy
            // hp do minusu jit nemuze a spravna (PvP) hodnota endangered je
            // proto vzdy false, bez ohledu na to, jestli je pokemon endangered
            // v overworldu.
            myPokemonView() {
                if (!this.fight || !this.fight.myPokemon || !this.myPokemonFull) {
                    return null;
                }
                return {...this.myPokemonFull, hp: this.fight.myPokemon.hp, maxHp: this.fight.myPokemon.maxHp, endangered: false};
            },
            opponentPokemonView() {
                if (!this.fight || !this.fight.opponentPokemon || !this.opponentPokemonFull) {
                    return null;
                }
                return {...this.opponentPokemonFull, hp: this.fight.opponentPokemon.hp, maxHp: this.fight.opponentPokemon.maxHp, endangered: false};
            },
            // Zadani uzivatele 2026-08-19 - picker nesmi nabizet pokemony
            // uz pouzite v tomhle zapase (backend to i tak blokne 400,
            // ale hrac by to zjistil az po kliku - viz myUsedPokemonIds
            // v PvpPresenter::fightToArray()).
            //
            // Zadani uzivatele 2026-08-20 - "v pokemon pickeru u PVP zobraz
            // vsechny pokemony jako full HP, aby to nepletlo a museji tim
            // padem byt picknutelni" - PvpService::choosePokemon() nasazuje
            // pokemona VZDY s plnym HP (setChallengerHp($pokemon->getMaxHp()),
            // realne HP z overworldu se pro PvP vubec nepouziva/nekontroluje).
            // `activeTeam` ale nese realne (treba nizke/0) HP z posledniho
            // singleplayer souboje - bez teto upravy by karta ukazovala
            // zavadejici cislo A navic PokemonPickerCard.vue disabluje
            // (pointer-events:none) karty s hp<1, takze realne vyfaintenej
            // pokemon by v PvP pickeru sel vybrat i presto, ze PvP ho
            // nasadi s plnym zivotem.
            pokemonsToChoose() {
                // Ranked vybira z VSECH pokemonu (tym + box u Oaka) - zadani
                // uzivatele 2026-08-27, "picker nabizi jen ty co mam u sebe,
                // ale mel by nabizet i ty u Oaka". Ranked vyzaduje presny
                // level (viz requiredLevel nize), ktery aktivni tym (max 6
                // pokemonu) nemusi mit - box ano. Volne PvP zustava jen na
                // aktivnim tymu (`activeTeam`), tam zadne omezeni na level neni.
                const source = this.isRanked ? (this.allPokemons || []) : (this.activeTeam || []);
                const usedIds = this.fight ? (this.fight.myUsedPokemonIds || []) : [];
                // Ranked zapas vyzaduje presny level (match.requiredPokemonLevel) -
                // BE stejne vynuti 400, ale nema smysl nabizet nevalidni volbu.
                const requiredLevel = this.match ? this.match.requiredPokemonLevel : null;
                return source
                    .filter((p) => usedIds.indexOf(p.id) === -1)
                    .filter((p) => requiredLevel === null || requiredLevel === undefined || p.level === requiredLevel)
                    .map((p) => ({...p, hp: p.maxHp}));
            },
            // "Stahnout pokemona" dava smysl jen kdyz uz mam v aktualnim
            // sub-fightu pokemona rozehraneho (ne v choose modu), zapas je
            // na vic pokemonu, a mam jeste aspon jednoho nepouziteho na
            // lavicce (zadani uzivatele 2026-08-20).
            canRecall() {
                if (this.chooseMode || !this.match || !this.fight || this.match.matchSize <= 1) {
                    return false;
                }
                const used = this.fight.myUsedPokemonIds || [];
                return used.length < this.match.matchSize && this.pokemonsToChoose.length > 0;
            },
            // Razeni: nepouzite skilly nejvyssim rateDmg napřed, pouzite
            // (disabled) abecedne za nimi - presna analogie BattleScreen.vue's
            // getFightSkills()/sortByBestDmg() (zadani uzivatele 2026-08-20 -
            // "picker má nabízet ten skill, který je podle rating nejvíc dmg",
            // stejne jako v normalnim souboji). rateDmg dorazi az od backendu
            // (viz PokemonTypeSkill::getRateDmg() - skupina "pokemon-manager"
            // pridana soubezne, jinak by tu bylo vzdy undefined).
            currentRoundSkills() {
                if (!this.fight || !this.fight.myPokemon || !this.myPokemonFull || !this.myPokemonFull.pokemonType) {
                    return [];
                }
                const wantDefence = !this.fight.iAmAttacker;
                const usedIds = this.fight.myUsedSkillIds || [];
                const skills = this.myPokemonFull.pokemonType.skills
                    .filter((s) => this.fight.myPokemon.skillIds.indexOf(s.id) !== -1)
                    .filter((s) => (s.defence > 0) === wantDefence)
                    .map((s) => ({
                        ...s,
                        disabled: usedIds.indexOf(s.id) !== -1,
                        formula: s.attackFormula + (s.dmgFormula ? ' / ' + s.dmgFormula : ''),
                    }));
                // LUNGE bonus do razeni, kdyz je souper prave stunnuty
                // (zadani uzivatele 2026-08-21) - stejna logika jako
                // BattleScreen.vue's sortByBestDmg(), zobrazeny rateDmg se
                // nemeni, meni se jen poradi v listu.
                const effectiveDmg = (s) => s.rateDmg + (this.opponentStunnedForLunge && s.effect === 'LUNGE' ? s.effectBonus : 0);
                const unused = skills.filter((s) => !s.disabled).sort((a, b) => effectiveDmg(b) - effectiveDmg(a));
                const used = skills.filter((s) => s.disabled)
                    .sort((a, b) => a.formula.toUpperCase().localeCompare(b.formula.toUpperCase()));
                return unused.concat(used);
            },
            // Adaptery na sdilenou FightReport.vue/FightReportDuel.vue (zadani
            // uzivatele 2026-08-18 - "musi byt naprosto stejny jako v normalnim
            // souboji"). Prekladaji tenci PvP lastRoundReport (viz
            // PvpService::resolveRound()) na presne stejny tvar dat, jaky
            // ocekava singleplayerovy FightReport2 - "trainer"/"attacker"
            // terminologie singleplayeru je tu prelozena na "ja (mySide)"/
            // "utocnik teto raundy", stejnou logikou, jakou driv pouzival
            // rucne psany markup (attackerSide === mySide).
            fightReportAdapter() {
                const lastReport = this.lastReport;
                const attackerIsMe = !!lastReport && lastReport.attackerSide === this.mySide;
                return {
                    // Finalni whole-branch review (opus) - fight.round je
                    // per-sub-fight (resetuje se na 1 po vymene pokemona),
                    // ale zalozka Historie cisluje kola prubezne napric
                    // celym zapasem (viz PvpService::resolveRound() ->
                    // addRoundLogEntry). Zadani uzivatele 2026-08-19 -
                    // "sjednotit, ať kolečko ukazuje průběžné číslo" -
                    // kolecko tedy pocita z match.roundLog.length (kolik
                    // kol uz je zaznamenanych napric celym zapasem) + 1,
                    // misto fight.round. Zadna zmena backendu/API - round
                    // se dal pouziva jinde (increment logika v
                    // PvpService::resolveRound()) presne jak driv.
                    round: (this.match && this.match.roundLog) ? this.match.roundLog.length + 1 : (this.fight ? this.fight.round : 1),
                    trainerPokemon: this.myPokemonView ? {name: this.myPokemonView.name} : null,
                    opponentPokemon: this.opponentPokemonView ? {name: this.opponentPokemonView.name} : null,
                    fightEnvironment: null,
                    bossFight: false,
                    active: true,
                    beforeAttackRound: attackerIsMe,
                    // Zadani uzivatele 2026-08-19 - "historie nemela byt
                    // prazdna" - drivejsi natvrdo [] nahrazeno skutecnym
                    // roundLog z backendu (viz PvpMatch::$roundLog,
                    // PvpPresenter::toDetail()). Tvar {round,text,tags} sedi
                    // primo na to, co FightReportHistory.vue ceka - zadna
                    // transformace netreba.
                    roundLog: (this.match && this.match.roundLog) || [],
                    fightReport2: {
                        beforeFight: !lastReport,
                        attSkill: lastReport ? this.findSkillObject(lastReport.attackerSkillId, attackerIsMe) : null,
                        defSkill: lastReport ? this.findSkillObject(lastReport.defenderSkillId, !attackerIsMe) : null,
                        attDice: lastReport ? lastReport.attDice : [],
                        defDice: lastReport ? lastReport.defDice : [],
                        attBonusDice: lastReport ? (lastReport.attBonusDice || []) : [],
                        attValue: lastReport ? lastReport.attValue : 0,
                        defValue2: lastReport ? lastReport.defValue2 : 0,
                        typeDmgMultiplier: lastReport ? lastReport.typeDmgMultiplier : 1,
                        defIneffective: lastReport ? lastReport.defIneffective : false,
                        defIneffectiveValue: lastReport ? lastReport.defIneffectiveValue : 0,
                        trainerPokemonDmg: lastReport ? (attackerIsMe ? lastReport.attackerDelta : lastReport.defenderDelta) : 0,
                        opponentPokemonDmg: lastReport ? (attackerIsMe ? lastReport.defenderDelta : lastReport.attackerDelta) : 0,
                        // Zadani uzivatele 2026-08-21 - STUN sdileny s PvE
                        // (viz PvpService::resolveRound()/CombatRoundEffects).
                        stunned: lastReport ? !!lastReport.stunned : false,
                        stunAttemptRoll: lastReport ? lastReport.stunAttemptRoll : null,
                        stunThreshold: lastReport ? lastReport.stunThreshold : null,
                    },
                };
            },
            // Presna analogie BattleScreen.vue's nextRoundHint - jen misto
            // fight.attackRound/trainerPokemon/opponentPokemon pouziva PvP
            // ekvivalenty (fight.iAmAttacker, myPokemonView/opponentPokemonView).
            nextRoundHint() {
                if (!this.fight || !this.lastReport || !this.lastReport.stunned) {
                    return null;
                }
                const skippedPokemon = this.fight.iAmAttacker ? this.opponentPokemonView : this.myPokemonView;
                const skippedName = skippedPokemon ? skippedPokemon.name : '';
                return this.fight.iAmAttacker
                    ? skippedName + ' ' + this.$t('common.stunnedAttackAgainSuffix')
                    : skippedName + ' ' + this.$t('common.stunnedTurnSkippedSuffix');
            },
            // Presna analogie BattleScreen.vue's opponentStunnedForLunge -
            // souper je prave stunnuty a hrac utoci znovu, LUNGE skilly se
            // podle toho maji predrazovat v razeni (viz currentRoundSkills).
            opponentStunnedForLunge() {
                return !!(this.fight && this.fight.iAmAttacker && this.lastReport && this.lastReport.stunned);
            },
            isTrainerFightAdapter() {
                if (!this.match) {
                    return null;
                }
                return {
                    usedPokemons: this.match.myUsedPokemonsCount || 0,
                    opponentUsedPokemons: this.match.opponentUsedPokemonsCount || 0,
                    maxPokemons: this.match.matchSize,
                };
            },
        },
        watch: {
            // Zadani uzivatele 2026-08-18 (rematch tlacitko na FINISHED
            // obrazovce) - router-view v App.vue nema :key, takze navigace
            // z /pvp/A na /pvp/B (stejna route componenta) instanci NErecykluje,
            // jen zmeni `id` prop. Bez tohohle watcheru by zustal viset stary
            // `match` (dohrany souboj) na obrazovce, dokud by nedorazil refresh.
            id: {
                handler() {
                    this.match = null;
                    this.myPokemonFull = null;
                    this.opponentPokemonFull = null;
                    this.errorMessage = '';
                    this.staleFightDetected = false;
                    this.newChallengeMatch = null;
                    this.refresh();
                },
            },
            'fight.myPokemon.id': {
                handler(id) {
                    if (id) {
                        this.hydratePokemon(id, 'myPokemonFull');
                    } else {
                        this.myPokemonFull = null;
                    }
                },
            },
            'fight.opponentPokemon.id': {
                handler(id) {
                    if (id) {
                        this.hydratePokemon(id, 'opponentPokemonFull');
                    } else {
                        this.opponentPokemonFull = null;
                    }
                },
            },
        },
        created() {
            this.loadActiveTeam(this.user ? this.user.id : null);
            this.loadAllPokemons();
            this.refresh();
            this.unsubscribePvpUpdate = mercureClient.onMessage('pvp_update', this.onPvpUpdateEvent);
        },
        beforeDestroy() {
            if (this.unsubscribePvpUpdate) {
                this.unsubscribePvpUpdate();
            }
        },
        methods: {
            ...mapActions({
                loadPvpMatchAction: 'trainer/loadPvpMatch',
                loadPvpMatchesAction: 'trainer/loadPvpMatches',
                acceptPvpAction: 'trainer/acceptPvp',
                declinePvpAction: 'trainer/declinePvp',
                challengePvpAction: 'trainer/challengePvp',
                choosePvpPokemonAction: 'trainer/choosePvpPokemon',
                choosePvpSkillAction: 'trainer/choosePvpSkill',
                surrenderPvpAction: 'trainer/surrenderPvp',
                recallPvpPokemonAction: 'trainer/recallPvpPokemon',
                loadPokemons: 'trainer/loadPokemons',
                loadActiveTeam: 'pokemon/loadActiveTeam',
                fetchPokemon: 'pokemon/fetchObject',
            }),
            loadAllPokemons() {
                this.fetchPokemon(pokemonApi.pokemonListAll(this.user ? this.user.id : null)).then((data) => {
                    this.allPokemons = (data && data.member) || [];
                });
            },
            refresh() {
                this.loadPvpMatchAction(this.matchId).then((data) => {
                    if (data) {
                        this.applyMatch(data);
                    } else {
                        // Zadani uzivatele 2026-08-20 - "reprodukuj a oprav:
                        // druhy hrac da refresh na mrtvem zapasu, tocí se
                        // nekonecne kolecko" - PUVODNI oprava (catch nize)
                        // NEFUNGOVALA, protoze trainer/loadPvpMatch (viz
                        // store/modules/trainer/actions.js) ma vlastni
                        // `.catch(() => null)` - nikdy nerejectne, jen vrati
                        // null. GET tedy "selze" tiše a `data` je null (napr.
                        // stara PvpMatch radka byla mezitim prunnuta - viz
                        // PvpService::pruneOldMatches, vola se pri KAZDE nove
                        // vyzve od stejneho souperе). Bez tehle vetve
                        // zustaval `match` navzdy null (spinner je
                        // `v-if="!match"` v template).
                        this.handleDeadMatch();
                    }
                });
            },
            // Zadani uzivatele 2026-08-20 - viz komentar v refresh() vyse.
            // Zkusime rovnou najit jiny aktivni/cekajici zapas se stejnym
            // souperem (pokud ho jeste zname z drivejsiho uspesneho
            // applyMatch - `this.match` pri neuspechu refreshe zustava, jak
            // byl) a presmerovat tam, jinak padneme zpet na PvP arenu.
            // trainer/loadPvpMatches ma stejny vzor (.catch(() => [])), takze
            // i tahle akce vzdy resolvne - prazdny seznam proste znamena
            // "nenalezeno", zadny dalsi catch tu neni potreba.
            handleDeadMatch() {
                const otherTrainerId = this.otherTrainer && this.otherTrainer.id ? this.otherTrainer.id : null;
                this.loadPvpMatchesAction().then((matches) => {
                    const active = (matches || []).find((m) =>
                        m.id !== this.matchId
                        && (m.status === 'ACTIVE' || m.status === 'PENDING')
                        && (otherTrainerId === null || (m.otherTrainer && m.otherTrainer.id === otherTrainerId))
                    );
                    if (active) {
                        this.$router.replace({name: 'PvpBattleScreen', params: {id: active.id}});
                    } else {
                        this.$router.replace({name: 'PvpHub'});
                    }
                });
            },
            // Zadani uzivatele 2026-08-20 - "yeat bell" kdyz je souboj
            // otevreny a naskoci nove kolo, at vim ze se neco deje primo
            // v tomhle okne (na rozdil od boxing-bell v TeamBottomSheet.vue,
            // ktery hraje na KAZDOU zmenu stavu zapasu odjinud). match.roundLog.length
            // je stejny prubezny pocitadlo kol jako ve fightReportAdapter()
            // nize (na rozdil od fight.round se neresetuje pri vymene
            // pokemona). wasSelf (pvpSelfActionTracker, stejny mechanismus
            // jako TeamBottomSheet.vue) vynechava zvuk, kdyz je kolo
            // dokoncene MOJI vlastni akci (i kdyz jsem ten, kdo tim koncí
            // kolo jako druhy hrac) - PvpService::notify() se vola po
            // KAZDE mutaci (i te, ktera jen ceka na soupere), takze vlastni
            // "cekaci" push konzumuje priznak drive, nez prijde ten skutecne
            // souperem zpusobeny.
            onPvpUpdateEvent(payload) {
                if (payload.matchId !== this.matchId) {
                    // Zadani uzivatele 2026-08-20 - push o JINEM zapase, nez
                    // na jaky se prave divame - zajima nas jen kdyz jsme na
                    // dohranem FINISHED zapase a soupere odsud nas mezitim
                    // vyzval znovu (viz checkForNewChallenge).
                    this.checkForNewChallenge(payload.matchId);
                    return;
                }
                const wasSelf = consumePvpSelfAction(this.matchId);
                const roundBefore = (this.match && this.match.roundLog) ? this.match.roundLog.length : 0;
                this.loadPvpMatchAction(this.matchId).then((data) => {
                    if (!data) {
                        return;
                    }
                    this.applyMatch(data);
                    const roundAfter = data.roundLog ? data.roundLog.length : 0;
                    if (!wasSelf && roundAfter > roundBefore) {
                        this.playNewRoundSound();
                    }
                });
            },
            playNewRoundSound() {
                // Rizeno prepinacem "Notifications" (stejne jako boxing-bell
                // v TeamBottomSheet.vue), ne "Sounds" sliderem (buttonVolume) -
                // to je samostatna vec (efekty/kliky), proto vzdy na plnou.
                if (!this.user || this.user.notificationsEnabled === false) {
                    return;
                }
                playNotificationSound('yeat-bell.mp3', 1);
            },
            // Zadani uzivatele 2026-08-20 - "kdyz mezitim protivnik me znovu
            // vyzve, tak by se mi mela dole refreshnout okynko vyhral jsi a
            // zobrazi se tam vyzva k zapasu... jen od stejneho soupere".
            // Filtrovano na: dohrany zapas na obrazovce (jinak neni co
            // prepisovat), nova PENDING vyzva (ne muj vlastni odchozi
            // challenge - data.isChallenger by pak bylo true), od stejneho
            // trenera, ktereho tu prave vidime jako otherTrainer.
            checkForNewChallenge(newMatchId) {
                if (!this.match || this.match.status !== 'FINISHED') {
                    return;
                }
                this.loadPvpMatchAction(newMatchId).then((data) => {
                    if (!data || data.status !== 'PENDING' || data.isChallenger) {
                        return;
                    }
                    if (!data.challenger || !this.otherTrainer || data.challenger.id !== this.otherTrainer.id) {
                        return;
                    }
                    this.newChallengeMatch = data;
                }).catch(() => {});
            },
            acceptNewChallenge() {
                if (!this.newChallengeMatch) {
                    return;
                }
                const newMatchId = this.newChallengeMatch.id;
                this.isBusy = true;
                this.acceptPvpAction(newMatchId).then(() => {
                    this.isBusy = false;
                    this.$router.push({name: 'PvpBattleScreen', params: {id: newMatchId}});
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            declineNewChallenge() {
                if (!this.newChallengeMatch) {
                    return;
                }
                const newMatchId = this.newChallengeMatch.id;
                this.isBusy = true;
                this.declinePvpAction(newMatchId).then(() => {
                    this.isBusy = false;
                    this.newChallengeMatch = null;
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            applyMatch(data) {
                const fight = data.fight;
                this.match = data;
                if (!fight || !fight.myPendingSet) {
                    this.autoSelectSkill();
                } else {
                    this.selectedSkillId = null;
                }
            },
            // Automaticky vybrat nejlepsi pouzitelny skill (zadani uzivatele
            // 2026-08-20 - "skillpicker nevybírá automaticky skilly stejně
            // jako to dělá kdekoliv jinde", pozdeji upresneno na "má to být
            // ten skill který je podle rating nejvíc dmg") - analogie
            // BattleScreen.vue's getFirstSkill(). currentRoundSkills uz radi
            // nepouzite skilly nejvyssim rateDmg napred, takze staci prvni
            // nedisabled polozka. Volana z applyMatch() (misto Vue watcheru
            // na `fight` prop, ktery tu neexistuje - fight je tu computed z
            // this.match) i po hydratePokemon() nize, protoze currentRoundSkills
            // zavisi na myPokemonFull, ktery se dohydruje asynchronne AZ PO
            // applyMatch() (viz watch: 'fight.myPokemon.id').
            autoSelectSkill() {
                // currentRoundSkills uz je serazeny nejlepsi-prvni - vezme prvni
                // nepouzitou polozku, ktere HP-prah (viz isHpGateSatisfied)
                // nebrani (zadani uzivatele 2026-08-28 - "protoze moje
                // nejsilnejsi skill je heal, tak se predvybere, ale dokud
                // nemam pod prah zivotu, tak by se mel z predvyberu
                // ignorovat"); kdyz jsou vsechny pouzitelne skilly gated a
                // nesplnene, spadne zpet na prvni nepouzitou vubec.
                const hp = this.fight && this.fight.myPokemon ? this.fight.myPokemon.hp : null;
                const maxHp = this.fight && this.fight.myPokemon ? this.fight.myPokemon.maxHp : null;
                const unused = this.currentRoundSkills.filter((s) => !s.disabled);
                const eligible = unused.find((s) => isHpGateSatisfied(s.effect, hp, maxHp));
                const best = eligible || unused[0];
                this.selectedSkillId = best ? best.id : null;
            },
            hydratePokemon(id, target) {
                this.loadPokemons([id]).then((list) => {
                    if (list && list.length) {
                        this[target] = list[0];
                        if (target === 'myPokemonFull') {
                            this.autoSelectSkill();
                        }
                    }
                });
            },
            doAccept() {
                this.isBusy = true;
                this.acceptPvpAction(this.matchId).then((data) => {
                    this.isBusy = false;
                    this.applyMatch(data);
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            // Zadani uzivatele 2026-08-19 - "kdyz prohraju/vyhraju a mam tam
            // zpet... ten cudlik nic neudela". $router.back() spolehal na
            // existenci polozky v historii prohlizece PRED timhle zapasem -
            // pokud sem hrac prisel primym odkazem/refreshem (zadna historie
            // v ramci SPA session), back() tise neudelal nic. Nahrazeno
            // explicitni navigaci na znamou route, funguje vzdy.
            goToHub() {
                this.$router.push({name: 'PvpHub'});
            },
            doRematch() {
                this.isBusy = true;
                this.errorMessage = '';
                this.challengePvpAction({receiverId: this.otherTrainer.id, matchSize: this.match.matchSize})
                    .then((data) => {
                        this.isBusy = false;
                        this.$router.push({name: 'PvpBattleScreen', params: {id: data.id}});
                    })
                    .catch((e) => {
                        this.isBusy = false;
                        this.errorMessage = e.message;
                    });
            },
            doDecline() {
                this.isBusy = true;
                this.declinePvpAction(this.matchId).then((data) => {
                    this.isBusy = false;
                    this.applyMatch(data);
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            async doSurrender() {
                const rlySurrender = await this.$refs.confirmDialog.open(this.$t('pvp.battleScreen.confirmSurrender'));
                if (!rlySurrender) {
                    return;
                }
                this.isBusy = true;
                this.errorMessage = '';
                this.surrenderPvpAction(this.matchId).then((data) => {
                    this.isBusy = false;
                    this.applyMatch(data);
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            async doRecall() {
                const rlyRecall = await this.$refs.confirmDialog.open(this.$t('pvp.battleScreen.confirmRecall'));
                if (!rlyRecall) {
                    return;
                }
                this.isBusy = true;
                this.errorMessage = '';
                this.recallPvpPokemonAction(this.matchId).then((data) => {
                    this.isBusy = false;
                    this.applyMatch(data);
                }).catch((e) => {
                    this.isBusy = false;
                    this.errorMessage = e.message;
                });
            },
            onChoosePokemon(pokemon) {
                this.choosePvpPokemonAction({matchId: this.matchId, pokemonId: pokemon.id}).then((data) => {
                    this.applyMatch(data);
                }).catch((e) => {
                    this.errorMessage = e.message;
                });
            },
            useSelectedSkill() {
                this.submitSkill(this.selectedSkillId);
            },
            useBaseMove() {
                this.submitSkill(null);
            },
            submitSkill(skillId) {
                if (this.isResolving) {
                    return;
                }
                this.isResolving = true;
                this.choosePvpSkillAction({matchId: this.matchId, skillId}).then((data) => {
                    this.isResolving = false;
                    this.applyMatch(data);
                }).catch((e) => {
                    this.isResolving = false;
                    this.errorMessage = e.message;
                    // Zadani uzivatele 2026-08-20 - "chyba pri odesilani
                    // zadosti, pokud jsem ve starem kombatu" - odeslani tahu
                    // muze selhat, protoze lokalni `fight` uz neodpovida
                    // aktualnimu stavu na backendu (napr. mezitim probehl
                    // sub-fight po vyfainteni pokemona a lokalni obrazovka
                    // jeste nedostala push). NEdela se tichy auto-refresh
                    // (druhe kolo zadani - "muzeme se vyzvat navzajem a ten
                    // jeden pak ostrouha") - misto attack tlacitek se zobrazi
                    // vyzva ke kliknuti (viz staleFightDetected v template),
                    // zbytek obrazovky zustava beze zmeny az do potvrzeni.
                    this.staleFightDetected = true;
                });
            },
            acceptStaleFight() {
                this.staleFightDetected = false;
                this.refresh();
            },
            // Pouzito jen fightReportAdapter() vyse - hleda plny skill objekt
            // (jmeno/typ/spread/defence/dmgBonus...) v uz hydratovanem
            // pokemonType.skills, protoze backend lastRoundReport posila jen
            // skillId (viz komentar u importu FightReport nahore).
            findSkillObject(skillId, mine) {
                if (skillId === null || skillId === undefined) {
                    return null;
                }
                const pokemonFull = mine ? this.myPokemonFull : this.opponentPokemonFull;
                if (!pokemonFull || !pokemonFull.pokemonType) {
                    return null;
                }
                return pokemonFull.pokemonType.skills.find((sk) => sk.id === skillId) || null;
            },
        },
    }
</script>

<style scoped>
/* Lesni pozadi (zadani uzivatele 2026-08-19 - "pridej tomu fightu obrazek
   v lese"), zatim staticke/pevne (ne podle mista hrace - to by vyzadovalo
   biome na kazdem meste/dungeonu, mimo scope tehle zmeny, viz konverzace).
   Zkousene drive: `wild/FOREST.png` (mala travel-tile thumbnail) a
   `position/FIGHT_BIOMS/WILD_FOREST.png` (obecny wild-encounter biome) -
   oboje spatne, uzivatel dodal vlastni ucelovy obrazek bojiste v lese
   (`battlefields/vlese.jpg`), zkopirovano do assets/pic/pvp/battlefield-forest.jpg.
   Karty souboje uz maji poloprůhledne pozadi (transparent03), takze lesni
   scena skrz ne prosviti misto plain barvy za nimi.
   Pozn.: samotny obrazek pozadi uz tady v CSS pravidle neni - dodava ho
   computed `battlefieldBackgroundStyle` navazany pres `:style` na root div v
   sablone (viz vyse). */
.pvp-battle-screen {
    height: 100%;
    display: flex;
    flex-direction: column;
    background-size: cover;
    background-position: center;
    /* Rezerva pro fixni TeamBottomSheet lištu dole (zadani uzivatele
       2026-08-19 - "choose pokemon text je lehounce prekryvany spodni
       listou... o 5-10px"). `.pvp-battle-screen` (stejne jako klasicka
       BattleScreen.vue) sedi primo v `height:100vh` `.v-main`, ktery s fixni
       46px vysokou team-sheet-peek listou (z-index 900) vubec nepocita -
       driv 0px rezervy, listy tak sedely presne na okraji viewportu, pod
       fixni listou. 12px -> 22px -> 17px -> 14px (zadani uzivatele
       2026-08-19, treti kolo - "posun to jeste o 3px") - 14px je presne
       kalibrovane minimum (46px handle - 32px VH_SAFETY_MARGIN_PX, main.js),
       zadna dalsi komfortni rezerva navic, listu porad nic nezakryva, ale
       uz to nejde zmensit dal beze zmeny samotne kalibrace. */
    padding-bottom: 14px;
    box-sizing: border-box;
}
.pvp-battle-screen-active {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
}
.pvp-battle-screen-active .battle-layout {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
}
/* Poloprůsvitný themovaný rámeček za textem/tlačítky (zadání uživatele
   2026-08-19 - "text by měl být v poloprůsvitném rámečku, aby byl lépe
   vidět, jako vždy poloprůsvitný bude dark/light themed") - text dřív seděl
   přímo na lesním pozadí, na světlejších částech obrázku špatně čitelný.
   Stejná konvence jako zbytek appky (transparent03/--dark podle
   $vuetify.theme.dark, viz TrainerTile.vue/FightReport.vue apod.). */
.pvp-battle-screen-status-card {
    display: inline-block;
    border-radius: 12px;
}
.pvp-battle-screen-status-text {
    font-size: 15px;
    margin-bottom: 16px;
}
.pvp-battle-screen-status-title {
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 4px;
}
.pvp-battle-screen-status-title--win {
    color: #43A047;
}
.pvp-battle-screen-status-title--lose {
    color: #E53935;
}
/* Sjednoceno s "choose pokemon" pokeball ctverecek (zadani uzivatele
   2026-08-19 - "pruhlednost choose pokemon ramecku a čeka na hrace je
   uplne rozdilna... sjednotit at maji stejnou pruhlednost"). Driv plna
   neprusvitna Vuetify barva karty + `opacity:0.8` na celem boxu (dimovalo
   i text) - vizualne uplne jiny efekt nez `.skin-pokemon-card`
   (CombatantSlot kind="choose"), ktera uz ma rgba(0.3) pozadi (viz
   public/skins/normal.css) - presne stejna hodnota jako transparent03/--dark
   tady. */
.pvp-battle-screen-waiting-card {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 16px;
    border-radius: 16px;
}
.pvp-battle-screen-finished-bar {
    padding: 10px 12px;
    text-align: center;
    border-radius: 12px;
}
</style>
